import {
  Controller,
  useForm,
} from 'react-hook-form';
import {
  LoginFormData,
} from '../page';
import Input from '@/components/Input';
import Wrapper from '@/components/Input/Wrapper';
import DatePicker from '@/core/components/DateTimePicker/SingleDatePicker';
import PhoneInput from '@/core/components/PhoneInput';
import Button from '@/components/Button';
import {
  useSendOTPForLogin,
  useSendMagicLink,
  useCheckPasswordlessStatus,
  useGetToken,
  useGetAuthLoginOptions,
} from '@/hooks/useApi';
import {
  normalizePhoneNumber,
} from '@/utils/helper';
import {
  formatDateOnly,
} from '@/utils/date';
import useLoginProvider from '@/contexts/LoginContext';
import {
  useContext,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  SignalRHubContext,
} from '@/core/context/signalRHub';
import device from '@/utils/device';
import {
  toast,
} from '@/hooks/useToast';
import {
  handleTokenResponse,
  setSignalRToken,
} from '@/utils/auth';
import {
  ApproveLoginFailedResponseDTO,
  ApproveLoginResponseDTO,
  ApproveLoginSuccessResponseDTO,
  LoginRequestDeliveryMethods,
  PatientOrgMappingDTO,
  SendMagicLinkFailedResponseDTO,
  SendMagicLinkSuccessResponseDTO,
  SendOTPAccountLockedDTO,
  SendOTPSuccessResponseDTO,
} from '@/dtos';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import Skeleton from '@/core/components/Skeleton';
import {
  isEmptyString,
} from '@/utils/misc';

const HELP_TEXT = {
  otp: 'A one-time code will be sent to your phone number (or email if landline) to verify your identity.',
  accessLink: 'A secure access link will be sent to your phone number (or email if landline) to verify your identity.',
  both: 'A one-time code or secure access link will be sent to your mobile number (or email if landline) to verify your identity.',
} as const;

export default function Login() {
  const { reconnect, hubConnection, disconnect } = useContext(SignalRHubContext);
  const {
    setLoginData,
    loginData,
    setPageState,
    setOrganizations,
    setLoginRequestIdHash,
    handleOrgSelection,
    setOtpResponse,
    setAuthLoginOptions,
  } = useLoginProvider();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<LoginFormData>({
    defaultValues: {
      firstName: loginData?.firstName ?? '',
      lastName: loginData?.lastName ?? '',
      dateOfBirth: loginData?.dateOfBirth ?? undefined,
      phoneNumber: loginData?.phoneNumber ?? '',
    },
  });

  const { data: authLoginOptions, isLoading: isLoadingAuthLoginOptions } = useGetAuthLoginOptions();

  useEffect(() => {
    if (!isNil(authLoginOptions)) {
      setAuthLoginOptions(authLoginOptions);
    }
  }, [authLoginOptions, setAuthLoginOptions]);

  const { mutateAsync: sendOTP, isPending: isSendingOTP } = useSendOTPForLogin();
  const { mutateAsync: sendMagicLink, isPending: isSendingMagicLink, isSuccess: isMagicLinkSent } = useSendMagicLink();
  const { mutateAsync: checkStatus } = useCheckPasswordlessStatus();
  const { mutateAsync: getToken, isPending: isGettingToken } = useGetToken();

  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingAttemptsRef = useRef(0);
  const maxPollingAttempts = 8; // 2 minutes / 15 seconds = 8 attempts
  const pollingInterval = 15000; // 15 seconds

  const magicLinkResponseRef = useRef<SendMagicLinkSuccessResponseDTO | null>(null);

  const isAnyActionInProgress = isSendingOTP || isSendingMagicLink || isPolling || isGettingToken || isMagicLinkSent;
  const disabled = isAnyActionInProgress || !isValid;

  const helpText = useMemo(() => {
    const isOTPEnabled = authLoginOptions?.isOTPEnabled;
    const isAccessLinkEnabled = authLoginOptions?.isAccessLinkEnabled;

    let key: keyof typeof HELP_TEXT = 'otp';

    if (isOTPEnabled && isAccessLinkEnabled) {
      key = 'both';
    } else if (isOTPEnabled) {
      key = 'otp';
    } else if (isAccessLinkEnabled) {
      key = 'accessLink';
    }

    return HELP_TEXT[key];
  }, [authLoginOptions?.isOTPEnabled, authLoginOptions?.isAccessLinkEnabled]);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    pollingAttemptsRef.current = 0;
    magicLinkResponseRef.current = null; // Clear stored response
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleLoginTokenReceived = async (data: ApproveLoginResponseDTO) => {
    console.log('SignalR: SendLoginTokenToDevice event received');

    if (magicLinkResponseRef.current) {
      try {
        const { deviceAuthToken } = magicLinkResponseRef.current;
        const response = await checkStatus({ deviceAuthToken });

        // Check if the response is a success response
        if ('patientOrgMappings' in response && 'loginRequestIdHash' in response) {
          const successResponse = response as ApproveLoginSuccessResponseDTO;
          const { patientOrgMappings, loginRequestIdHash } = successResponse;
          if ((patientOrgMappings?.length ?? 0) > 0 && !isNil(loginRequestIdHash)) {
            await handleCheckStatusResult(patientOrgMappings, loginRequestIdHash);
          }
        } else {
          // Failed response
          const failedResponse = response as ApproveLoginFailedResponseDTO;
          toast.error(failedResponse.reason ?? 'Failed to check login status. Please try again.');
        }
      } catch (error) {
        console.error('SignalR: Error checking passwordless status:', error);
      }
    } else {
      console.warn('SignalR: No magic link response data available');
    }
  };

  useEffect(() => {
    if (!isNil(hubConnection)){
      hubConnection.on('OnLoginApproved', handleLoginTokenReceived);
    }
    return () => {
      if (!isNil(hubConnection)){
        hubConnection.off('OnLoginApproved', handleLoginTokenReceived);
      }
    };
  }, [hubConnection]);

  const onGetCode =  async (data: LoginFormData) => {
    const phoneNumber = normalizePhoneNumber(data.phoneNumber);
    const dateOfBirth = formatDateOnly(data.dateOfBirth ?? '');

    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: dateOfBirth,
      phoneNumber: phoneNumber,
    };

    const otpResponse = await sendOTP(payload);

    // Check if the response is a success or account locked
    if ('requestHash' in otpResponse) {
      // Success - user received OTP
      const successResponse = otpResponse as SendOTPSuccessResponseDTO;
      setOtpResponse(successResponse);
      setLoginData(payload);
      setPageState('otp');
    } else {
      // Account locked
      const lockedResponse = otpResponse as SendOTPAccountLockedDTO;
      toast.error(lockedResponse.errorMessage ?? 'Account is locked. Please contact support.');
    }
  };

  const handleCheckStatusResult = async (
    patientOrgMappings: PatientOrgMappingDTO[] | null | undefined,
    loginRequestIdHash: string,
  ) => {
    stopPolling();
    setLoginRequestIdHash(loginRequestIdHash);

    // If there's only one organization, automatically log in
    if (patientOrgMappings?.length === 1) {
      try {
        const org = head(patientOrgMappings);
        const tokenResponse = await getToken({
          loginRequestIdHash: loginRequestIdHash,
          patientIdHash: org?.patientHash ?? null,
          organizationIdHash: org?.organizationHash ?? null,
        });

        if (tokenResponse?.token && !isNil(org)) {
          // Store token, refreshToken, and sessionHash
          handleTokenResponse(tokenResponse);

          // Disconnect SignalR connection and clear SignalR token
          await disconnect();
          console.log('SignalR connection disconnected and token cleared after successful login');

          toast.success('Login successful! Redirecting...');
          await handleOrgSelection(org, tokenResponse);
        } else {
          toast.error('Failed to get authentication token. Please try again.');
        }
      } catch (error) {
        console.error('Error getting token:', error);
        toast.error('Failed to complete login. Please try again.');
      }
    } else {
      // Multiple organizations - show organization selection
      setOrganizations(patientOrgMappings ?? []);
      setPageState('orgSelection');
      toast.info('Please select your organization to continue.');
    }
  };

  // Start polling for status
  const pollStatus = async (deviceAuthToken?: string) => {
    try {
      pollingAttemptsRef.current += 1;

      if (pollingAttemptsRef.current > maxPollingAttempts) {
        stopPolling();
        toast.error('Login request timed out. Please try again.');
        return;
      }

      const response = await checkStatus({ deviceAuthToken });

      // Check if the response is a success response
      if ('patientOrgMappings' in response && 'loginRequestIdHash' in response) {
        const { patientOrgMappings, loginRequestIdHash } = response as ApproveLoginSuccessResponseDTO;
        if ((patientOrgMappings?.length ?? 0) > 0 && !isNil(loginRequestIdHash)) {
          await handleCheckStatusResult(patientOrgMappings, loginRequestIdHash);
        }
      }
    } catch (error) {
      console.error('Error checking passwordless status:', error);
    }
  };

  const onSendMagicLink = async (data: LoginFormData) => {
    const phoneNumber = normalizePhoneNumber(data.phoneNumber);
    const dateOfBirth = formatDateOnly(data.dateOfBirth ?? '');
    const deviceId = device.getOrCreateDeviceId();

    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: dateOfBirth,
      phoneNumber: phoneNumber,
      deviceId: deviceId,
    };

    try {
      const response = await sendMagicLink(payload);

      // Check if the response is a success or failure
      if ('deviceAuthToken' in response) {
        // Success response
        const successResponse = response as SendMagicLinkSuccessResponseDTO;
        const { deviceAuthToken, deliveryType, emailAddress, expiryMinutes = 5 } = successResponse;
        magicLinkResponseRef.current = successResponse;
        const sentViaEmail = deliveryType === LoginRequestDeliveryMethods.Email;
        const destination = sentViaEmail && !isEmptyString(emailAddress)
          ? `your email at ${emailAddress}`
          : 'your phone';
        toast.success(`Access link sent! Please check ${destination} and use the link within ${expiryMinutes} minutes.`);

        setLoginData(payload);

        let isSignalRConnected = false;

        if (deviceAuthToken) {
          setSignalRToken(deviceAuthToken);

          try {
            // Reconnect returns the new connection directly
            const newConnection = await reconnect(deviceAuthToken);

            // Check the connection state directly from the returned connection
            if (newConnection && newConnection.state === 'Connected') {
              isSignalRConnected = true;
              console.log('✓ SignalR connection established successfully');
            } else {
              isSignalRConnected = false;
              console.warn('⚠ SignalR connection not established. State:', newConnection?.state || 'No connection');
            }
          } catch (error) {
            console.error('✗ Error establishing SignalR connection:', error);
            isSignalRConnected = false;
          }
        }

        if (!isSignalRConnected) {
          console.log('→ Starting polling as fallback (SignalR not connected)');
          setIsPolling(true);
          pollingAttemptsRef.current = 0;
          await pollStatus(deviceAuthToken ?? '');

          pollingIntervalRef.current = setInterval(pollStatus, pollingInterval);
        } else {
          console.log('→ Using SignalR events for login approval (polling disabled)');
        }
      } else {
        // Failed response
        const failedResponse = response as SendMagicLinkFailedResponseDTO;
        if (failedResponse.isLocked) {
          toast.error(`Account is locked. ${failedResponse.reason ?? 'Please contact support.'}`);
        } else {
          toast.error(failedResponse.reason ?? 'Failed to send magic link. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error sending magic link:', error);
      stopPolling();
    }
  };

  return (
    <div className="flex flex-col space-y-6 populate-form login-form">
      <div className="flex flex-col gap-4">
        <Controller
          name="firstName"
          control={control}
          rules={{ required: 'First name is required' }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <Input
              id="firstName"
              label="First Name"
              labelClasses="text-xs md:text-sm font-normal text-gray-700 mb-0.5"
              showErrorText={false}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error}
              className="w-full"
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: 'Last name is required' }}
          render={({ field: { onChange, value, onBlur }, fieldState: { invalid } }) => (
            <Input
              id="lastName"
              label="Last Name"
              labelClasses="text-xs md:text-sm font-normal text-gray-700 mb-0.5"
              showErrorText={false}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={invalid}
              className="w-full"
            />
          )}
        />
        <Controller
          name="dateOfBirth"
          control={control}
          rules={{
            required: 'Date of birth is required',
          }}
          render={({ field: { onChange, value, onBlur }, fieldState: { invalid } }) => (
            <Wrapper
              id="dateOfBirth"
              label="Date of Birth"
              showErrorText={false}
              error={invalid}
              labelClasses="text-xs md:text-sm font-normal text-gray-700 mb-0.5"
            >
              <DatePicker
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disableFuture={true}
                inputFormat="MM/DD/YYYY"
                className="w-full"
                containerClassName="w-full"
                showDateButtons={false}
              />
            </Wrapper>
          )}
        />
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: 'Phone number is required',
            pattern: {
              value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/,
              message: 'Please enter a valid phone number',
            },
          }}
          render={({ field: { onChange, value, onBlur }, fieldState: { invalid } }) => (
            <Wrapper
              id="phoneNumber"
              label="Phone Number"
              showErrorText={false}
              error={invalid}
              labelClasses="text-xs md:text-sm font-normal text-gray-700 mb-0.5"
            >
              <PhoneInput
                id="phoneNumber"
                name="phoneNumber"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                required
                error={invalid}
                className="w-full"
                inputClassName="w-full"
              />
            </Wrapper>
          )}
        />
      </div>
      <div className='flex gap-4'>
        {isLoadingAuthLoginOptions ? (
          <>
            <Skeleton className="w-full !h-12 md:!h-10 !rounded-lg" />
            <Skeleton className="w-full !h-12 md:!h-10 !rounded-lg" />
          </>
        ) : (
          <>
            {authLoginOptions?.isOTPEnabled && (
              <Button
                variant="login"
                size="login"
                disabled={disabled}
                onClick={handleSubmit(onGetCode)}
                className="w-full !h-12 md:!h-10 disabled:opacity-50 bg-primary hover:bg-primary/80 text-white font-medium py-2 rounded-lg text-sm md:text-base"
              >
                {isSendingOTP ? 'Sending...' : 'Get Code'}
              </Button>
            )}
            {authLoginOptions?.isAccessLinkEnabled && (
              <Button
                variant="login"
                size="login"
                disabled={disabled}
                onClick={handleSubmit(onSendMagicLink)}
                className="w-full !h-12 md:!h-10 disabled:opacity-50 bg-[#242424] hover:bg-[#242424]/80 text-white font-medium py-2 rounded-lg text-sm md:text-base"
              >
                {isSendingMagicLink ? 'Sending...' : (isPolling || isMagicLinkSent) ? 'Verifying Access...' : isGettingToken ? 'Logging in...' : 'Get Access Link'}
              </Button>
            )}
          </>
        )}
      </div>
      {isLoadingAuthLoginOptions ? (
        <div className="!mt-2 flex flex-col gap-1">
          <Skeleton className="w-full !rounded-lg" />
          <Skeleton className="w-full !rounded-lg" />
        </div>
      ) : (
        <p className="!mt-4 text-center text-xs text-gray-800 leading-5 max-w-md mx-auto">
          {helpText}
        </p>
      )}
    </div>
  );
}
